/**
 * Steuerberater-Exporte (DATEV)
 * 
 * Features:
 * - DATEV-Export
 * - GoBD-Archivierung
 * - Monatsberichte
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet, Download, Archive, Calendar, RefreshCw,
  CheckCircle, AlertCircle, FileText, Euro, TrendingUp
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Monate für Dropdown
const MONTHS = [
  { value: 1, label: 'Januar' },
  { value: 2, label: 'Februar' },
  { value: 3, label: 'März' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Dezember' }
];

export default function TaxExports() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  
  // Selected period
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  
  // Data
  const [summary, setSummary] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [archiveStatus, setArchiveStatus] = useState(null);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/login');
      return;
    }
    loadData();
  }, [sessionToken, selectedYear, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      const [summaryRes, reportRes, archiveRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tax-export/summary?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        fetch(`${BACKEND_URL}/api/tax-export/monthly-report?year=${selectedYear}&month=${selectedMonth}`, { headers }),
        fetch(`${BACKEND_URL}/api/tax-export/gobd-archive`, { headers })
      ]);
      
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (reportRes.ok) setMonthlyReport(await reportRes.json());
      if (archiveRes.ok) setArchiveStatus(await archiveRes.json());
      
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadDatev = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/tax-export/datev-export?year=${selectedYear}&month=${selectedMonth}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DATEV_Export_${selectedYear}_${selectedMonth.toString().padStart(2, '0')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success('DATEV-Export heruntergeladen');
      } else {
        toast.error('Export fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const createArchive = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/tax-export/gobd-archive?year=${selectedYear}&month=${selectedMonth}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );
      
      if (response.ok) {
        toast.success('GoBD-Archiv erstellt');
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Archivierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Archivierung fehlgeschlagen');
    }
  };

  // Years for dropdown
  const years = [];
  for (let y = currentDate.getFullYear(); y >= currentDate.getFullYear() - 3; y--) {
    years.push(y);
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Header */}
      <header className="bg-[#111C32] border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-emerald-400" />
              Steuerberater-Exporte
            </h1>
            <p className="text-sm text-slate-400">
              DATEV-Export & GoBD-Archivierung
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="flex flex-wrap gap-2">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24 bg-slate-800 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Summary Cards */}
        {summary?.summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#111C32] border-slate-700">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Brutto-Umsatz</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(summary.summary.total_revenue_gross)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#111C32] border-slate-700">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Netto-Umsatz</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {formatCurrency(summary.summary.total_revenue_net)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#111C32] border-slate-700">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">MwSt gesamt</p>
                <p className="text-2xl font-bold text-amber-400">
                  {formatCurrency(summary.summary.total_vat)}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-[#111C32] border-slate-700">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Bestellungen</p>
                <p className="text-2xl font-bold text-white">
                  {summary.summary.order_count}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* DATEV Export */}
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-400" />
                DATEV-Export
              </CardTitle>
              <CardDescription className="text-slate-400">
                Buchungsstapel für Ihren Steuerberater
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-400 mb-4">
                CSV-Datei im DATEV-Format. Kann direkt in DATEV importiert werden.
              </p>
              <Button onClick={downloadDatev} className="w-full bg-blue-600 hover:bg-blue-700">
                <Download className="w-4 h-4 mr-2" />
                DATEV-Export herunterladen
              </Button>
            </CardContent>
          </Card>

          {/* GoBD Archive */}
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Archive className="w-5 h-5 text-amber-400" />
                GoBD-Archivierung
              </CardTitle>
              <CardDescription className="text-slate-400">
                Revisionssichere Archivierung
              </CardDescription>
            </CardHeader>
            <CardContent>
              {archiveStatus?.available_periods?.find(
                p => p.year === selectedYear && p.month === selectedMonth
              )?.archived ? (
                <div className="flex items-center gap-2 text-emerald-400 mb-4">
                  <CheckCircle className="w-5 h-5" />
                  <span>Dieser Monat ist archiviert</span>
                </div>
              ) : (
                <p className="text-sm text-slate-400 mb-4">
                  Archivieren Sie diesen Monat für die GoBD-Konformität.
                </p>
              )}
              <Button 
                onClick={createArchive} 
                variant="outline"
                className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                disabled={archiveStatus?.available_periods?.find(
                  p => p.year === selectedYear && p.month === selectedMonth
                )?.archived}
              >
                <Archive className="w-4 h-4 mr-2" />
                {archiveStatus?.available_periods?.find(
                  p => p.year === selectedYear && p.month === selectedMonth
                )?.archived ? 'Bereits archiviert' : 'Monat archivieren'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* MwSt Breakdown */}
        {summary?.summary && (
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">MwSt-Aufschlüsselung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">19% MwSt (Vor-Ort)</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Brutto:</span>
                      <span className="text-white">{formatCurrency(summary.summary.vat_19?.gross)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Netto:</span>
                      <span className="text-white">{formatCurrency(summary.summary.vat_19?.net)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">MwSt:</span>
                      <span className="text-amber-400 font-bold">{formatCurrency(summary.summary.vat_19?.vat)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="font-medium text-white mb-2">7% MwSt (Außer-Haus)</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Brutto:</span>
                      <span className="text-white">{formatCurrency(summary.summary.vat_7?.gross)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Netto:</span>
                      <span className="text-white">{formatCurrency(summary.summary.vat_7?.net)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">MwSt:</span>
                      <span className="text-amber-400 font-bold">{formatCurrency(summary.summary.vat_7?.vat)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Breakdown */}
        {monthlyReport?.daily_breakdown?.length > 0 && (
          <Card className="bg-[#111C32] border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Tagesübersicht</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Datum</TableHead>
                      <TableHead className="text-slate-400 text-right">Bestellungen</TableHead>
                      <TableHead className="text-slate-400 text-right">Bar</TableHead>
                      <TableHead className="text-slate-400 text-right">Karte</TableHead>
                      <TableHead className="text-slate-400 text-right">Gesamt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyReport.daily_breakdown.map((day) => (
                      <TableRow key={day.date} className="border-slate-700">
                        <TableCell className="text-white">{day.date}</TableCell>
                        <TableCell className="text-right text-slate-300">{day.orders}</TableCell>
                        <TableCell className="text-right text-slate-300">{formatCurrency(day.cash)}</TableCell>
                        <TableCell className="text-right text-slate-300">{formatCurrency(day.card)}</TableCell>
                        <TableCell className="text-right text-emerald-400 font-bold">{formatCurrency(day.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

      </main>
    </div>
  );
}
